import React, { useState } from 'react';

const operationStyles = {
  create: { color: 'var(--success)', backgroundColor: 'rgba(16, 185, 129, 0.1)', label: 'Created' },
  update: { color: 'var(--accent)', backgroundColor: 'rgba(var(--primary-rgb), 0.12)', label: 'Updated' },
  delete: { color: 'var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.1)', label: 'Deleted' }
};

const formatFieldName = (field) => (
  String(field || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
);

const stringifyValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

const summarizeValue = (value, maxLength = 120) => {
  const text = stringifyValue(value);
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}...`;
};

const formatJsonBlock = (value) => {
  try {
    return JSON.stringify(value || {}, null, 2);
  } catch (error) {
    return String(value || '');
  }
};

const buildFallbackDisplayChanges = (entry, fields) => (
  fields.map(field => {
    const change = entry.changes?.[field] || {};
    return {
      field,
      label: formatFieldName(field),
      type: 'field',
      summary: `${formatFieldName(field)} changed`,
      before_text: summarizeValue(change.before),
      after_text: summarizeValue(change.after)
    };
  })
);

const ChangeHistoryTimeline = ({ entries = [], loading = false, emptyMessage = 'No change history yet.', showSequence = false }) => {
  const [openDetails, setOpenDetails] = useState({});

  if (loading) {
    return <div className="card" style={{ padding: '1rem', textAlign: 'center' }}>Loading change history...</div>;
  }

  if (!entries.length) {
    return (
      <div className="card" style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>
        {emptyMessage}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem' }}>
      {entries.map((entry, index) => {
        const tone = operationStyles[entry.operation] || operationStyles.update;
        const fields = entry.changed_fields?.length ? entry.changed_fields : Object.keys(entry.changes || {});
        const displayChanges = entry.display_changes?.length
          ? entry.display_changes
          : buildFallbackDisplayChanges(entry, fields);
        const detailKey = entry._id || `${entry.document_id}-${entry.version}`;
        const isOpen = !!openDetails[detailKey];
        const sequenceNumber = index + 1;

        return (
          <div key={detailKey} style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: '0.85rem' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '9999px', backgroundColor: tone.color, marginTop: '0.4rem', boxShadow: `0 0 0 4px ${tone.backgroundColor}` }} />
              {index !== entries.length - 1 && (
                <div style={{ width: '2px', flex: 1, background: 'linear-gradient(to bottom, var(--border-light), transparent)', marginTop: '0.35rem' }} />
              )}
            </div>

            <div style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-light)', borderRadius: '8px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--border-light)', backgroundColor: 'var(--bg-soft)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      {showSequence && (
                        <span style={{ padding: '0.22rem 0.5rem', borderRadius: '9999px', fontSize: '0.68rem', fontWeight: 800, color: 'var(--text-secondary)', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-light)' }}>
                          Step {sequenceNumber}
                        </span>
                      )}
                      <span style={{ padding: '0.22rem 0.58rem', borderRadius: '9999px', fontSize: '0.68rem', fontWeight: 700, color: tone.color, backgroundColor: tone.backgroundColor }}>
                        {tone.label}
                      </span>
                      <span style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {entry.display_title || entry.document_label || entry.document_id}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', wordBreak: 'break-word' }}>
                      {entry.display_module_label || entry.module_label || entry.module} / v{entry.version}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {new Date(entry.created_at).toLocaleString()}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      {entry.actor_username || 'system'}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ padding: '0.95rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
                <div style={{ backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.7rem 0.8rem', color: 'var(--text-primary)', fontSize: '0.82rem', fontWeight: 700 }}>
                  {entry.display_summary || `${tone.label} ${displayChanges.length} field${displayChanges.length === 1 ? '' : 's'}`}
                </div>

                {displayChanges.map(change => (
                  <div key={`${detailKey}-${change.field}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '0.45rem', alignItems: 'start' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', wordBreak: 'break-word' }}>
                        {change.label || formatFieldName(change.field)}
                      </div>
                      {change.summary && (
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{change.summary}</span>
                      )}
                    </div>

                    {change.type === 'provider_list' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', borderRadius: '6px', padding: '0.35rem 0.5rem', fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
                            Before: {change.before_text}
                          </span>
                          <span style={{ backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', borderRadius: '6px', padding: '0.35rem 0.5rem', fontSize: '0.74rem', color: 'var(--text-secondary)' }}>
                            After: {change.after_text}
                          </span>
                        </div>
                        {(change.items || []).map((item, itemIndex) => (
                          <div key={`${detailKey}-${change.field}-${itemIndex}`} style={{ border: '1px solid var(--border-light)', borderRadius: '8px', padding: '0.65rem 0.75rem', backgroundColor: 'var(--bg-card)' }}>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: item.details?.length ? '0.4rem' : 0 }}>
                              <span style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', color: item.type === 'removed' ? 'var(--error)' : item.type === 'added' ? 'var(--success)' : 'var(--accent)' }}>
                                {item.summary}
                              </span>
                              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-primary)' }}>{item.label}</span>
                            </div>
                            {item.details?.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                                {item.details.map(detail => (
                                  <span key={`${detailKey}-${change.field}-${itemIndex}-${detail}`} style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', borderRadius: '9999px', padding: '0.16rem 0.45rem' }}>
                                    {detail}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.55rem', alignItems: 'stretch', fontSize: '0.76rem' }}>
                        <div>
                          <div style={{ marginBottom: '0.25rem', fontSize: '0.66rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>Before</div>
                          <code style={{ display: 'block', minHeight: '34px', backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', borderRadius: '6px', padding: '0.4rem 0.5rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {change.before_text || '-'}
                          </code>
                        </div>
                        <div>
                          <div style={{ marginBottom: '0.25rem', fontSize: '0.66rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>After</div>
                          <code style={{ display: 'block', minHeight: '34px', backgroundColor: 'var(--bg-soft)', border: '1px solid var(--border-light)', borderRadius: '6px', padding: '0.4rem 0.5rem', color: 'var(--text-primary)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {change.after_text || '-'}
                          </code>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', paddingTop: '0.2rem' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    {entry.route || 'System change'}{entry.request_sequence ? ` | Action step ${entry.request_sequence}` : ''}{entry.ip_address ? ` | ${entry.ip_address}` : ''}
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setOpenDetails(prev => ({ ...prev, [detailKey]: !isOpen }))}
                    style={{ minHeight: '32px', padding: '0.35rem 0.65rem', fontSize: '0.75rem' }}
                  >
                    {isOpen ? 'Hide Details' : 'Show Details'}
                  </button>
                </div>

                {isOpen && (
                  <pre style={{ margin: 0, padding: '0.8rem', border: '1px solid var(--border-light)', borderRadius: '8px', fontSize: '0.73rem', lineHeight: 1.55, color: 'var(--text-primary)', backgroundColor: 'var(--bg-soft)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '320px', overflow: 'auto' }}>
                    {formatJsonBlock(entry.changes)}
                  </pre>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ChangeHistoryTimeline;
